import { MapPin, DollarSign, Building2, TrendingUp, Home, Layers, Landmark, Scale, Calendar, Users } from 'lucide-react';

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const createSuburbArticles = (suburb, suburbSlug, config) => {
  const { distance, keyInfra, zones, mainStreets, priceRange, keyDriver, uniqueFactor, comparison, nearbyAmenity } = config;
  return [
    {
      id: `${suburbSlug}-property-development-site-guide`,
      category: 'Local Guide',
      title: `Is Your ${suburb} Property a Development Site? Signs to Look For`,
      excerpt: `${suburb} is one of Brisbane's most sought-after inner-city locations. Here's how to tell if your property has development potential.`,
      date: '2026-04-01',
      readTime: '7 min read',
      image: img('1697767217116-465fa27e4521'),
      icon: MapPin,
      metaDescription: `Is your ${suburb} property a development site? Learn the signs that your land has development potential in this premium Brisbane inner-city suburb ${distance} from the CBD.`,
      content: `
## ${suburb}: Inner-City Development Potential

${suburb} sits just ${distance} from the Brisbane CBD and is one of the most tightly held suburbs in the city. ${uniqueFactor}

If you own property in ${suburb}, there's a good chance it could be worth significantly more as a development site than as a residential home.

## Key Signs Your ${suburb} Property Has Development Value

### 1. Your Zoning Allows Higher Density

Under the Brisbane City Plan 2014, ${suburb} contains several zones:

| Zone | Typical Height | What Can Be Built |
|---|---|---|
${zones.map(z => `| ${z.name} | ${z.height} | ${z.product} |`).join('\n')}

Check your specific zoning at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/).

### 2. Your Block is Suitable for Development

Developers in ${suburb} look for:
- **Blocks of 400m²+** in medium-to-high density zones
- **Wide frontage** (15m+ preferred) for efficient building design
- **Corner blocks** with dual street frontage
- **Flat or gently sloping** topography

### 3. You're Near Key Infrastructure

${keyInfra}

### 4. Developers Have Already Approached You

If you've received unsolicited letters or approaches from developers, it's a strong signal — but never accept without independent advice. A specialist agent creates competition that drives significantly higher prices.

> **Pro tip**: Properties in ${suburb} that would sell for a standard residential price regularly achieve 40-120% more when marketed as development sites to qualified developer buyers.

## What's Driving Developer Demand in ${suburb}

- ${keyDriver}
- Strong end-buyer demand for inner-city apartment living
- Limited remaining development sites in the suburb
- ${nearbyAmenity}
- Sustained population growth and housing undersupply

## How to Find Out Your Property's Potential

ACRES provides complimentary development site assessments for ${suburb} property owners. We assess your zoning, constraints, and realistic development yield to give you an informed view of market value.

## Frequently Asked Questions

**Q: How much more could my ${suburb} property be worth as a development site?**
Properties in medium-to-high density zones typically sell for 40-120% above standard residential value. The premium depends on zoning, block size, and site characteristics.

**Q: Do I need approval before selling?**
No. Most developers prefer to design their own project. However, a site with an existing DA can attract a 10-20% premium.

**Q: How long will it take to sell?**
Development site sales typically take 2-4 months to secure a contract, with settlement periods of 6-18 months.

---

*ACRES provides complimentary development site assessments in ${suburb}. Call 07 3096 0542.*
      `,
      faq: [
        { q: `Is my ${suburb} property a development site?`, a: `If your property is zoned for medium or high density in ${suburb}, it likely has significant development value. Corner blocks and lots over 500m² are particularly attractive to developers.` },
        { q: `How much more is my ${suburb} land worth to a developer?`, a: `Properties in medium-to-high density zones can be worth 40-120% more as development sites. The premium depends on zoning, block size, proximity to infrastructure, and site characteristics.` },
        { q: `Are developers actively buying in ${suburb}?`, a: `Yes. ${suburb} is one of Brisbane's most active inner-city development markets, driven by ${keyDriver.toLowerCase()} and sustained end-buyer demand.` },
      ],
    },
    {
      id: `${suburbSlug}-land-prices-what-developers-pay`,
      category: 'Local Guide',
      title: `What Developers Are Paying for Land in ${suburb}`,
      excerpt: `Current pricing benchmarks for development sites in ${suburb} and what drives the numbers.`,
      date: '2026-04-28',
      readTime: '6 min read',
      image: img('1672643405411-10cdbebfc3df'),
      icon: DollarSign,
      metaDescription: `What are developers paying for land in ${suburb} Brisbane? Current development site pricing, recent sales data, and what drives land values in this inner-city suburb.`,
      content: `
## ${suburb} Development Site Pricing

${suburb} development sites are among the most valuable in Brisbane's inner ring. ${uniqueFactor} The combination of ${keyDriver.toLowerCase()} and limited remaining supply keeps pricing firm.

## Current Price Benchmarks

| Site Type | Typical Size | Price Range | Price per m² |
|---|---|---|---|
${priceRange.map(p => `| ${p.type} | ${p.size} | ${p.range} | ${p.perm2} |`).join('\n')}

These figures represent what developers will pay — often significantly above standard residential market value.

## What Drives Price Variation in ${suburb}

### Zoning and Height
The single biggest driver. Higher density zoning allows more apartments, generating more revenue, which means developers can pay more for the land.

### Location Within the Suburb
Properties on or near ${mainStreets} command premiums. Quieter residential streets may be less per m² but still attract strong developer interest.

### Site Configuration
Wide frontage, flat topography, and corner positions increase value. Narrow or steep sites attract fewer buyers.

### Existing Approvals
A site with a current DA typically sells for 10-20% more than an unapproved site.

## How ${suburb} Compares

${comparison}

> **Key insight**: Properties that would sell for a standard residential price regularly achieve significant premiums when marketed as development sites to the right buyer pool.

## Frequently Asked Questions

**Q: Are ${suburb} development site prices still rising?**
Prices have shown consistent growth driven by limited supply and strong demand. Structural factors support continued pricing strength.

**Q: How do I know if a developer's offer is fair?**
Never accept an unsolicited offer without independent advice. A specialist agent tests the broader market and creates competitive tension.

**Q: How do I get an accurate price for my ${suburb} site?**
Contact ACRES for a complimentary development site appraisal that considers zoning, yield, feasibility, and current developer demand.

---

*For a confidential ${suburb} development site appraisal, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `What are developers paying for land in ${suburb}?`, a: `Pricing varies by zone and site characteristics. ${priceRange[1] ? `${priceRange[1].type} sites sell for ${priceRange[1].range}.` : 'Contact ACRES for current benchmarks.'}` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Yes. Limited supply and strong demand continue to support price growth. ${suburb} remains one of Brisbane's most sought-after inner-city development markets.` },
        { q: `How much more will a developer pay vs a home buyer in ${suburb}?`, a: `Developers typically pay 40-120% above residential value depending on zoning. A specialist agent can provide an accurate assessment for your specific property.` },
      ],
    },
    {
      id: `${suburbSlug}-zoning-guide-what-you-can-build`,
      category: 'Local Guide',
      title: `${suburb} Zoning Guide: What You Can Build on Your Block`,
      excerpt: `A plain-English breakdown of ${suburb}'s zoning and what it means for your property's development value.`,
      date: '2026-04-25',
      readTime: '8 min read',
      image: img('1606509463400-5ce6440c53e9'),
      icon: Scale,
      metaDescription: `${suburb} Brisbane zoning guide. Understanding your property zoning, building heights, and development potential under the Brisbane City Plan 2014.`,
      content: `
## Understanding ${suburb}'s Zoning

${suburb}'s zoning is governed by the Brisbane City Plan 2014 and relevant neighbourhood plan provisions. Your zoning determines what can be built on your property — and critically, how much it's worth.

## Residential Zones in ${suburb}

${zones.map(z => `### ${z.name}
- **Height**: ${z.height}
- **What you can build**: ${z.product}
- **Development premium**: ${z.premium || 'Varies by site characteristics'}
`).join('\n')}

## How Zoning Affects Your Property Value

Consider two identical blocks in ${suburb}:

| Zoning | Potential Yield | Estimated Value |
|---|---|---|
| Low Density | 1 dwelling | Standard residential |
| Medium Density | 8-12 apartments | 50-80% premium |
| High Density | 20-40+ apartments | 100-150%+ premium |

Same block size — dramatically different values based on zoning alone.

## Overlays and Constraints

Key overlays affecting ${suburb} properties:
- **Flood overlay**: Some areas may have flood mapping requiring elevated construction
- **Heritage/Character**: Check for any heritage listings or character provisions
- **Infrastructure**: Easements and services that may affect developable area

## How to Check Your Zoning

1. Visit [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
2. Search your ${suburb} address
3. Check the Zoning, Overlays, and Neighbourhood Plan layers

## Frequently Asked Questions

**Q: How do I find my exact zoning in ${suburb}?**
Use Brisbane City Plan Interactive Mapping. Search your address and check the Zoning layer. For a professional interpretation, contact ACRES for a free assessment.

**Q: Can my zoning change?**
Yes. Council reviews the City Plan periodically. Properties near new infrastructure are candidates for future upzoning.

**Q: Does higher zoning guarantee maximum height?**
Not always. Overlays and neighbourhood plan provisions may limit achievable height.

---

*Not sure what your ${suburb} zoning means? ACRES provides free assessments. Call 07 3096 0542.*
      `,
      faq: [
        { q: `What zoning does my ${suburb} property have?`, a: `${suburb} has a mix of residential zones. Check cityplan.brisbane.qld.gov.au for your specific property. ACRES provides free zoning assessments.` },
        { q: `What can I build on my ${suburb} block?`, a: `It depends on your zone. ${zones[0] ? `${zones[0].name} allows ${zones[0].product.toLowerCase()}.` : ''} Higher zones allow apartments and mixed-use developments.` },
        { q: `Could my ${suburb} property be upzoned in the future?`, a: `Properties near new infrastructure and transport nodes are candidates for upzoning in future City Plan reviews.` },
      ],
    },
    {
      id: `${suburbSlug}-infrastructure-driving-property-values`,
      category: 'Local Guide',
      title: `How ${keyInfra.split('.')[0].replace(/^Properties near /, '').replace(/^The /, '')} Is Driving ${suburb} Property Values`,
      excerpt: `Key infrastructure and amenity driving development demand and property values in ${suburb}.`,
      date: '2026-04-22',
      readTime: '6 min read',
      image: img('1656501375517-67129e3eed14'),
      icon: TrendingUp,
      metaDescription: `How infrastructure and amenity drive ${suburb} property values. The impact on land prices, development demand, and what it means for property owners.`,
      content: `
## Infrastructure Driving ${suburb}'s Growth

${keyInfra}

For property owners in ${suburb}, infrastructure investment is the single most reliable indicator of future value growth. Every major transport, commercial, or community project in or near the suburb increases development potential and end-buyer demand.

## How Infrastructure Affects Development Site Values

| Infrastructure Type | Value Impact | Mechanism |
|---|---|---|
| Rail/Metro station | 10-25% uplift | Higher density allowances + end-buyer premium |
| Bus rapid transit | 5-15% uplift | Improved connectivity drives demand |
| Major retail/commercial | 5-10% uplift | Lifestyle amenity attracts buyers |
| Hospital/University | 8-15% uplift | Employment + student/staff housing demand |
| Olympic venues | 10-20% uplift | Government investment + precinct renewal |

Properties within walking distance of these catalysts see the greatest value increases.

## What This Means for ${suburb} Property Owners

The infrastructure driving ${suburb}'s growth is creating sustained demand from developers who need sites to deliver housing near these investments. If you own property in ${suburb}:

- Your land is likely worth more now than it was 3 years ago
- Development site premiums (above residential value) are increasing
- Multiple buyer types are competing for available sites
- Timing is favourable for sellers

> **Important**: Infrastructure-driven value growth typically continues for 5-10 years after project completion as the surrounding precinct matures and new amenity is established.

## Frequently Asked Questions

**Q: Has infrastructure already increased ${suburb} property values?**
Yes. Property values have risen significantly as infrastructure projects progress. Development site premiums are particularly strong.

**Q: Will values keep rising after projects are completed?**
Historical evidence suggests values continue rising for 5-10 years post-completion as precincts mature and development activity intensifies.

**Q: Should I sell now or wait for more infrastructure?**
Current conditions are favourable. Waiting carries both opportunity and risk. ACRES can advise on optimal timing.

---

*Understand how infrastructure affects your ${suburb} property. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How does infrastructure affect ${suburb} property values?`, a: `Major infrastructure increases surrounding property values by 5-25%. Development sites near transport and amenity see the greatest uplift.` },
        { q: `Will ${suburb} property values keep rising?`, a: `Infrastructure-driven growth typically continues 5-10 years after completion. Current fundamentals remain strong for ${suburb}.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable with strong demand and limited supply. The optimal timing depends on your personal circumstances.` },
      ],
    },
    {
      id: `selling-${suburbSlug}-home-to-developer-guide`,
      category: 'Local Guide',
      title: `Selling Your ${suburb} Home to a Developer: A Complete Guide`,
      excerpt: `Step-by-step process for selling your ${suburb} property as a development site for maximum value.`,
      date: '2026-04-18',
      readTime: '7 min read',
      image: img('1513880989635-6eb491ce7f5b'),
      icon: Home,
      metaDescription: `How to sell your ${suburb} home to a developer. Complete guide covering preparation, pricing, agent selection, and achieving the best development site price in ${suburb}.`,
      content: `
## Why ${suburb} Sellers Should Think Development

If your ${suburb} property sits on land zoned for medium or high density, selling to a developer could unlock significantly more value than a standard residential sale.

## Step 1: Understand Your Development Potential

- **Check your zoning** at cityplan.brisbane.qld.gov.au
- **Measure your block** — lot size, frontage, and shape affect yield
- **Note key features** — corner position, proximity to transport, main road frontage
- **Identify constraints** — overlays, easements, or heritage listings

## Step 2: Choose a Specialist Agent

Look for an agent with:
- Development site experience in ${suburb}
- An active database of developer buyers
- Knowledge of recent comparable sales
- A clear marketing strategy for developer buyers

> **Warning**: Unsolicited developer offers are almost always below market value. A specialist agent achieves 15-30% more through competitive tension.

## Step 3: Prepare Your Property

### Essential:
- **Boundary survey** ($2,000-$4,000)
- **Title search** and encumbrance review
- **Planning summary** with zoning and development potential

### Valuable additions:
- **Town planning assessment** ($3,000-$5,000)
- **Concept design** ($5,000-$10,000) — makes the opportunity tangible

Don't invest in house renovations — developers will demolish regardless.

## Step 4: Market Through an EOI Campaign

1. Agent contacts developer database directly
2. Information memorandum prepared with all site details
3. Site inspections with qualified buyers
4. Offer deadline creates competitive tension
5. Negotiate with strongest offers

## Step 5: Negotiate Terms

Beyond price, negotiate:
- **Deposit**: 5-10% (push for 10%)
- **Conditions**: DA, finance, due diligence with clear timeframes
- **Settlement**: 6-18 months typical
- **Sunset clause**: Deadline for condition satisfaction

## Frequently Asked Questions

**Q: Can I stay in my home during the sale process?**
Yes. You remain in the property throughout marketing and the conditional period. Settlement is typically 6-18 months after contract.

**Q: How long does the process take?**
Typically 2-4 months to contract, then 6-18 months conditional period, then 4-8 weeks to settlement.

**Q: What commission does a development site agent charge?**
Typically 1.5-2.5%. Given the significantly higher prices achieved, the net outcome is much better than a standard residential sale.

---

*Ready to explore your ${suburb} property's potential? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How do I sell my ${suburb} home to a developer?`, a: `Engage a specialist agent, prepare with survey and planning assessment, market through an EOI to create competitive tension, and negotiate terms as well as price.` },
        { q: `Can I live in my ${suburb} home while selling to a developer?`, a: `Yes. You remain in the property through marketing and the conditional period. Settlement is typically 6-18 months after contract.` },
        { q: `What commission does a development site agent charge in ${suburb}?`, a: `Typically 1.5-2.5%. Given premiums of 40-120% above residential value, the net outcome is significantly better.` },
      ],
    },
    {
      id: `${suburbSlug}-development-hotspots`,
      category: 'Local Guide',
      title: `${suburb} Development Hotspots: Where Developers Want to Buy`,
      excerpt: `The specific streets and pockets within ${suburb} attracting the strongest developer demand right now.`,
      date: '2026-04-15',
      readTime: '5 min read',
      image: img('1730329944265-ab697e1a631c'),
      icon: Layers,
      metaDescription: `${suburb} development hotspots. Which streets and areas are attracting the most developer interest in ${suburb} Brisbane?`,
      content: `
## Where Developers Are Buying in ${suburb}

Not all streets in ${suburb} are equal when it comes to development demand. Developers target specific pockets based on zoning, infrastructure proximity, and end-buyer appeal.

## Primary Hotspots

Properties along and near ${mainStreets} are the most sought-after, offering:
- Higher density zoning and height allowances
- Transport connectivity and amenity access
- Strong end-buyer demand for completed apartments
- Established commercial and lifestyle precincts

## Secondary Hotspots

Quieter residential streets in MDR zones offer:
- More affordable entry points for boutique developers
- Appeal to owner-occupier end-buyers seeking quieter locations
- Potential for medium-density townhouse and apartment projects

## What Makes a Street a Hotspot

| Factor | Impact on Developer Demand |
|---|---|
| HDR or Centre zoning | Very High — allows maximum yield |
| MDR zoning | High — sweet spot for many projects |
| Corner position | High — design and access advantages |
| Transport proximity | High — end-buyer premium |
| LMR zoning | Moderate — townhouse potential |

## Frequently Asked Questions

**Q: My property isn't on a main road — does it still interest developers?**
Yes. Many developers prefer quieter streets for residential projects. If your zoning supports medium density, your property has development value.

**Q: Are developers only buying large sites in ${suburb}?**
No. Standard 400-600m² blocks in suitable zones attract strong developer interest. Site consolidation is also common.

**Q: How do I know if my street has developer demand?**
Contact ACRES for a free assessment. We monitor developer activity across every ${suburb} street.

---

*Is your ${suburb} street a development hotspot? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Which ${suburb} streets are developers buying on?`, a: `Properties along ${mainStreets} see the strongest demand. Quieter MDR-zoned streets also attract boutique developers.` },
        { q: `Do developers buy on quiet streets in ${suburb}?`, a: `Yes. Many developers prefer quieter streets for residential projects targeting owner-occupiers.` },
        { q: `Are developers only buying large blocks in ${suburb}?`, a: `No. Standard 400-600m² blocks in MDR and HDR zones are actively sought. Consolidation with neighbours is also common.` },
      ],
    },
    {
      id: `future-of-${suburbSlug}-council-plans`,
      category: 'Local Guide',
      title: `The Future of ${suburb}: Council Plans & What They Mean for Landowners`,
      excerpt: `How evolving council policy and infrastructure investment are shaping ${suburb}'s development future.`,
      date: '2026-04-12',
      readTime: '6 min read',
      image: img('1672626881472-b96d3eb5445e'),
      icon: Landmark,
      metaDescription: `The future of ${suburb} Brisbane. How council planning changes and infrastructure investment are shaping the suburb and what it means for property owners.`,
      content: `
## ${suburb}'s Evolving Landscape

${suburb} continues to evolve as Brisbane grows and the demand for inner-city living intensifies. Council planning, infrastructure investment, and demographic shifts are all shaping the suburb's future — and your property's value.

## What's Driving Change

### Population Growth
South East Queensland's population continues to grow faster than any other Australian region. Inner-city suburbs like ${suburb} absorb a significant share of this growth through medium and high-density development.

### Infrastructure Investment
${keyInfra} These investments create sustained demand for housing and commercial space, driving development activity.

### Planning Evolution
Council periodically reviews the City Plan, and ${suburb} is likely to see continued refinement of density provisions, particularly along transport corridors and near major amenity.

## What This Means for Property Owners

- Properties in higher-density zones will continue to appreciate as a development asset
- Infrastructure investment near your property strengthens long-term value
- Limited remaining development sites mean increasing scarcity premium
- Current market conditions are favourable for sellers considering a development site sale

## Looking Ahead

| Timeframe | Expected Impact |
|---|---|
| 2025-2027 | Current infrastructure benefits materialise |
| 2027-2030 | Precinct maturation, continued strong demand |
| 2030-2035 | Established premium inner-city status |

## Frequently Asked Questions

**Q: Will ${suburb} be further upzoned?**
Possible, particularly near transport. Council reviews the City Plan periodically and population growth supports increased density in inner-city suburbs.

**Q: Should I wait for planning changes before selling?**
Current zoning already supports strong development values. Waiting for potential upzoning carries uncertainty. ACRES can advise on the best timing.

**Q: How do planning changes affect my property value?**
Density increases raise development site values. A specialist agent can assess the specific impact for your property.

---

*Understand ${suburb}'s future and your property's potential. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Will ${suburb} be further upzoned?`, a: `Possible, particularly near transport corridors. Council reviews the City Plan periodically and population growth supports density increases.` },
        { q: `What does the future hold for ${suburb} property values?`, a: `Strong fundamentals including population growth, infrastructure, and limited supply support continued value appreciation.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable. Waiting carries uncertainty. ACRES can advise on optimal timing for your circumstances.` },
      ],
    },
    {
      id: `${suburbSlug}-corner-blocks-large-lots`,
      category: 'Local Guide',
      title: `${suburb} Corner Blocks & Large Lots: Why Developers Pay More`,
      excerpt: `Corner blocks and larger lots in ${suburb} command significant premiums from developer buyers.`,
      date: '2026-04-08',
      readTime: '5 min read',
      image: img('1557518016-95747c0eef63'),
      icon: Building2,
      metaDescription: `Why developers pay more for corner blocks and large lots in ${suburb} Brisbane. Premium pricing and what makes your block attractive to developers.`,
      content: `
## The Premium for Corners and Large Lots in ${suburb}

In ${suburb}, where remaining development sites are increasingly scarce, corner blocks and large lots command significant premiums. These properties offer developers design, yield, and market advantages.

## Why Corner Blocks Are Worth More

- **Dual street frontage** allows separate pedestrian and vehicle access
- **Better apartment layouts** with more natural light and cross-ventilation
- **More attractive building forms** engaging with both streetscapes
- **Higher density allowances** under some planning provisions

Corner blocks in ${suburb} typically command a **20-35% premium** over comparable mid-block sites.

## Why Large Lots Are Valuable

Large lots (700m²+) offer:
- Greater development yield on a single title
- No amalgamation risk or neighbour negotiations
- More efficient building design
- Faster project commencement

## Site Consolidation

If you own property adjacent to another potential development site, a coordinated sale can increase total value by 10-20%. ACRES facilitates these discussions confidentially.

## Frequently Asked Questions

**Q: How much more is a corner block worth in ${suburb}?**
Typically 20-35% more than a comparable mid-block site in the same zone.

**Q: Is my standard block still a development site?**
Yes, if zoned for medium or high density. Even standard blocks attract developer interest.

**Q: Can I coordinate a sale with my neighbour?**
Yes. ACRES facilitates confidential multi-vendor sales for site consolidation.

---

*Own a corner block or large lot in ${suburb}? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more is a corner block worth in ${suburb}?`, a: `Typically 20-35% more than mid-block sites in the same zone.` },
        { q: `Are large lots particularly valuable in ${suburb}?`, a: `Yes. Large lots (700m²+) are rare and command significant premiums.` },
        { q: `Can I sell my ${suburb} property with my neighbour's?`, a: `Yes. ACRES facilitates confidential multi-vendor sales. Combined value is typically 10-20% higher.` },
      ],
    },
    {
      id: `${suburbSlug}-property-market-2025`,
      category: 'Local Guide',
      title: `${suburb} Property Market 2026: Development Site Demand & Pricing`,
      excerpt: `Current market conditions for ${suburb} development sites and where the market is heading.`,
      date: '2026-04-05',
      readTime: '6 min read',
      image: img('1659563159297-6f724e125026'),
      icon: Calendar,
      metaDescription: `${suburb} property market 2025. Development site demand, pricing trends, and market outlook for sellers in this premium Brisbane inner-city suburb.`,
      content: `
## Market Overview: ${suburb} 2025

${suburb}'s development site market in 2026 is characterised by strong demand and limited supply. The suburb's inner-city position, transport connectivity, and lifestyle appeal continue to attract developer interest across multiple buyer types.

## Who's Buying
- National and local developers seeking apartment sites
- Boutique operators targeting owner-occupier markets
- Build-to-rent operators seeking rental housing sites

## Market Conditions
- **Demand**: Strong — multiple active buyers for quality sites
- **Supply**: Limited — remaining development sites are scarce
- **Pricing**: Firm to rising — limited supply supports strong pricing
- **Off-market activity**: Increasing — many transactions occurring before public marketing

## Outlook

Current conditions are favourable for development site sellers in ${suburb}:
- Strong demand fundamentals
- Limited remaining supply
- Infrastructure investment continuing
- End-buyer market remains resilient

> **Bottom line**: ${suburb} is a seller's market for development sites. Well-prepared properties marketed through specialist agents are achieving premium outcomes.

## Frequently Asked Questions

**Q: Is 2025 a good time to sell a development site in ${suburb}?**
Yes. Demand is strong, supply is limited, and fundamentals continue to improve.

**Q: Will prices keep rising?**
Structural factors support continued pricing strength. Limited supply and sustained demand provide a strong floor.

**Q: How many developers are active in ${suburb}?**
ACRES maintains relationships with numerous active developers seeking sites in ${suburb} and surrounds.

---

*For a current ${suburb} market assessment, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Is 2025 a good time to sell in ${suburb}?`, a: `Yes. Strong demand, limited supply, and improving fundamentals make current conditions favourable for development site sellers.` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Prices are firm to rising. Limited remaining supply and sustained demand support continued pricing strength.` },
        { q: `How many developers are buying in ${suburb}?`, a: `Multiple national and local developers are actively seeking sites. Competition for well-located properties is strong.` },
      ],
    },
    {
      id: `downsizing-${suburbSlug}-selling-developer`,
      category: 'Local Guide',
      title: `Downsizing in ${suburb}: How Selling to a Developer Funds Your Next Chapter`,
      excerpt: `Long-term ${suburb} homeowners could unlock significant value by selling to a developer rather than a home buyer.`,
      date: '2026-04-01',
      readTime: '6 min read',
      image: img('1656501382301-9b560230018a'),
      icon: Users,
      metaDescription: `Downsizing in ${suburb} Brisbane by selling to a developer. How long-term homeowners can unlock premium development value to fund retirement.`,
      content: `
## Your ${suburb} Home Could Fund a Comfortable Retirement

If you've owned your ${suburb} home for many years, the suburb's growth means your property could be worth significantly more to a developer than to a traditional home buyer.

## The Opportunity

Properties in ${suburb}'s medium-to-high density zones regularly sell for 40-120% above standard residential value when marketed as development sites. The premium could mean hundreds of thousands of additional dollars.

## Tax Benefits

If the property has been your principal residence, the capital gain is generally **exempt from CGT**. The Downsizer Contribution allows individuals aged 55+ to contribute up to $300,000 ($600,000 per couple) into superannuation.

## Common Concerns

### "I don't want to leave ${suburb}"
You don't have to. Many downsizers purchase an apartment nearby and stay in the community they love.

### "The process sounds complicated"
A specialist agent manages the entire process. You continue living in your home throughout.

### "How long before I need to move?"
Settlement is typically 6-18 months after contract, giving you plenty of time to plan.

## Next Steps

1. Request a free, confidential assessment from ACRES
2. Compare development vs residential value
3. Take your time — no rush, just information
4. Plan your next chapter with financial clarity

## Frequently Asked Questions

**Q: How much more could I get selling to a developer?**
Properties in suitable zones typically sell for 40-120% above residential value.

**Q: Do I pay tax on the sale?**
If it's been your principal residence, the gain is generally exempt from CGT.

**Q: Can I stay in ${suburb}?**
Yes. Many downsizers purchase a nearby apartment while banking the premium.

---

*Considering downsizing from your ${suburb} home? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more could I get selling my ${suburb} home to a developer?`, a: `Properties in medium-to-high density zones typically sell for 40-120% above residential value.` },
        { q: `Do I pay CGT selling my ${suburb} home?`, a: `If it's been your principal residence for the entire ownership period, the gain is generally exempt from CGT.` },
        { q: `Can I stay in ${suburb} after selling?`, a: `Yes. Many downsizers purchase a nearby apartment and stay in the community they love.` },
      ],
    },
  ];
};

export const southBrisbaneArticles = createSuburbArticles('South Brisbane', 'south-brisbane', {
  distance: '1km south',
  keyInfra: 'Properties near South Brisbane train station, the Cultural Centre busway, and the South Bank precinct benefit from world-class transport and cultural infrastructure. The Grey Street precinct and Melbourne Street corridor are major activity centres.',
  zones: [
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 60-100% above residential' },
    { name: 'High Density Residential', height: '8-20+ storeys', product: 'Apartment towers', premium: 'Very high — 100-200%+ above residential' },
    { name: 'Centre / Mixed Use', height: '15-30+ storeys', product: 'Mixed-use towers, commercial + residential', premium: 'Maximum — highest land values in inner Brisbane' },
    { name: 'Special Purpose', height: 'Varies', product: 'Cultural, educational, government uses', premium: 'N/A — generally not available for private development' },
  ],
  mainStreets: 'Grey Street, Melbourne Street, Vulture Street, and Merivale Street',
  priceRange: [
    { type: 'Standard house (MDR)', size: '400-600m²', range: '$1.5M - $2.5M', perm2: '$3,000 - $4,500' },
    { type: 'Larger block (HDR)', size: '600-1,000m²', range: '$3.0M - $6.0M', perm2: '$4,000 - $6,500' },
    { type: 'Centre zone site', size: '1,000-2,000m²', range: '$6.0M - $20M+', perm2: '$5,000 - $10,000+' },
  ],
  keyDriver: 'Immediate CBD adjacency, South Bank cultural precinct, and major government employment centres',
  uniqueFactor: 'South Brisbane is the closest residential suburb to the CBD and sits directly adjacent to the South Bank cultural and lifestyle precinct — home to QPAC, GOMA, the State Library, and 17 hectares of riverside parklands.',
  comparison: 'South Brisbane commands the highest development site prices in Brisbane\'s inner south, reflecting its unmatched CBD proximity and cultural precinct access.',
  nearbyAmenity: 'Direct adjacency to South Bank Parklands, QPAC, GOMA, and Queensland Museum',
});

export const kangarooPointArticles = createSuburbArticles('Kangaroo Point', 'kangaroo-point', {
  distance: '1.5km east',
  keyInfra: 'Properties benefit from proximity to the Story Bridge, Captain Cook Bridge, and ferry terminals. The Kangaroo Point cliffs and riverside walks provide premium lifestyle amenity. The planned Green Bridge to the CBD will further enhance connectivity.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density Residential', height: '8-15+ storeys', product: 'Apartment buildings with river views', premium: 'Very high — 80-150%+ above residential' },
    { name: 'Mixed Use / Centre', height: '10-20+ storeys', product: 'Mixed-use towers', premium: 'Maximum — premium river-view sites' },
  ],
  mainStreets: 'Main Street, River Terrace, Shafston Avenue, and Leopard Street',
  priceRange: [
    { type: 'Standard house (MDR)', size: '400-600m²', range: '$1.3M - $2.0M', perm2: '$2,800 - $3,800' },
    { type: 'River-adjacent (HDR)', size: '600-800m²', range: '$2.5M - $5.0M', perm2: '$3,500 - $6,000' },
    { type: 'Premium river site', size: '800-1,500m²', range: '$4.0M - $10M+', perm2: '$5,000 - $8,000+' },
  ],
  keyDriver: 'Unmatched river views and CBD proximity, with the planned Kangaroo Point Green Bridge creating direct pedestrian access to the CBD',
  uniqueFactor: 'Kangaroo Point is defined by its dramatic cliff face overlooking the Brisbane River and CBD. The suburb offers some of the most spectacular views in Brisbane, making it a premium address for apartment development.',
  comparison: 'Kangaroo Point development sites command premiums for river views that few other suburbs can match. River-frontage sites are among the most valuable in Brisbane.',
  nearbyAmenity: 'River walks, Kangaroo Point cliffs, Story Bridge, and direct CBD ferry access',
});

export const fortitudeValleyArticles = createSuburbArticles('Fortitude Valley', 'fortitude-valley', {
  distance: '1.5km north-east',
  keyInfra: 'The Valley has excellent rail connectivity via Fortitude Valley train station and is served by multiple bus routes. The James Street precinct and Brunswick Street Mall are major lifestyle and commercial anchors.',
  zones: [
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments, boutique projects', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density Residential', height: '8-20+ storeys', product: 'Apartment towers', premium: 'Very high — 80-150%+ above residential' },
    { name: 'Centre / Mixed Use', height: '15-30+ storeys', product: 'Mixed-use towers, entertainment, retail + residential', premium: 'Maximum — Valley precinct premium' },
    { name: 'Special Entertainment', height: 'Varies', product: 'Entertainment venues, nightlife, dining', premium: 'Variable — depends on transition potential' },
  ],
  mainStreets: 'Brunswick Street, Ann Street, Wickham Street, and James Street',
  priceRange: [
    { type: 'Standard site (MDR)', size: '400-600m²', range: '$1.2M - $2.0M', perm2: '$2,500 - $3,500' },
    { type: 'HDR site', size: '600-1,000m²', range: '$2.5M - $5.0M', perm2: '$3,500 - $5,500' },
    { type: 'Centre zone site', size: '1,000-2,000m²', range: '$5.0M - $15M+', perm2: '$4,500 - $8,000+' },
  ],
  keyDriver: 'Brisbane\'s premier entertainment, dining, and nightlife precinct with strong young professional and investor demand',
  uniqueFactor: 'Fortitude Valley is Brisbane\'s most dynamic urban precinct — a 24-hour entertainment, dining, and cultural hub that attracts a young, affluent buyer demographic. The James Street precinct has established itself as Brisbane\'s premium lifestyle address.',
  comparison: 'The Valley commands strong development site prices driven by its unique lifestyle positioning and diverse buyer demand.',
  nearbyAmenity: 'James Street dining and retail precinct, Brunswick Street Mall, Chinatown, and Valley music venues',
});

export const newsteadArticles = createSuburbArticles('Newstead', 'newstead', {
  distance: '2.5km north-east',
  keyInfra: 'Newstead benefits from the Gasworks precinct, Breakfast Creek Road commercial corridor, and proximity to the Fortitude Valley and Teneriffe precincts. River ferry services and planned transport improvements enhance connectivity.',
  zones: [
    { name: 'Medium Density Residential', height: '5-8 storeys', product: 'Apartments, townhouses', premium: 'Significant — premium waterfront location' },
    { name: 'High Density Residential', height: '10-25+ storeys', product: 'Premium apartment towers', premium: 'Very high — waterfront premium' },
    { name: 'Centre / Mixed Use', height: '15-30+ storeys', product: 'Mixed-use towers, Gasworks precinct', premium: 'Maximum — Brisbane\'s premier apartment address' },
  ],
  mainStreets: 'Breakfast Creek Road, Skyring Terrace, and Commercial Road',
  priceRange: [
    { type: 'Standard site (MDR)', size: '400-600m²', range: '$1.5M - $2.5M', perm2: '$3,000 - $4,500' },
    { type: 'HDR site', size: '600-1,000m²', range: '$3.0M - $7.0M', perm2: '$4,500 - $7,000' },
    { type: 'Waterfront/Centre site', size: '1,000m²+', range: '$7.0M - $20M+', perm2: '$6,000 - $12,000+' },
  ],
  keyDriver: 'Brisbane\'s established premium waterfront apartment market with the Gasworks precinct anchoring lifestyle appeal',
  uniqueFactor: 'Newstead has established itself as Brisbane\'s premier apartment address. The Gasworks precinct and waterfront positioning attract premium end-buyers, with apartments regularly exceeding $1M. Remaining development sites are extremely rare.',
  comparison: 'Newstead commands the highest per-square-metre rates for apartment development sites in Brisbane, reflecting its premium waterfront positioning.',
  nearbyAmenity: 'Gasworks precinct, James Street, Brisbane River walks, and ferry terminals',
});

export const teneriffeArticles = createSuburbArticles('Teneriffe', 'teneriffe', {
  distance: '2.5km north-east',
  keyInfra: 'Teneriffe\'s heritage wool stores, converted to premium apartments and commercial spaces, form the suburb\'s architectural identity. River ferry services, proximity to James Street, and the New Farm Park precinct anchor lifestyle appeal.',
  zones: [
    { name: 'Low-Medium Density', height: '2-3 storeys', product: 'Townhouses, adaptive reuse', premium: 'Moderate — heritage considerations' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Boutique apartments, heritage conversions', premium: 'Significant — character premium' },
    { name: 'High Density Residential', height: '8-15+ storeys', product: 'Premium apartment buildings', premium: 'Very high — river and heritage premium' },
  ],
  mainStreets: 'Vernon Terrace, Commercial Road, Skyring Terrace, and Macquarie Street',
  priceRange: [
    { type: 'Standard house lot', size: '400-600m²', range: '$1.3M - $2.2M', perm2: '$2,800 - $4,000' },
    { type: 'MDR/HDR site', size: '600-1,000m²', range: '$2.5M - $5.0M', perm2: '$3,500 - $5,500' },
    { type: 'River-adjacent premium', size: '800m²+', range: '$4.0M - $8.0M+', perm2: '$5,000 - $8,000+' },
  ],
  keyDriver: 'Heritage character, river frontage, and established premium lifestyle positioning',
  uniqueFactor: 'Teneriffe is defined by its iconic heritage wool stores — massive brick warehouses converted into premium apartments and commercial spaces. This heritage character gives the suburb a unique identity and premium positioning in Brisbane\'s property market.',
  comparison: 'Teneriffe development sites command premiums for their heritage character and river positioning. Remaining opportunities are rare and competition is intense.',
  nearbyAmenity: 'Heritage wool store precinct, Brisbane River walks, New Farm Park, and James Street',
});

export const newFarmArticles = createSuburbArticles('New Farm', 'new-farm', {
  distance: '2km north-east',
  keyInfra: 'New Farm benefits from the Brisbane Powerhouse cultural precinct, New Farm Park (one of Brisbane\'s most popular riverside parks), and proximity to the Fortitude Valley and Teneriffe lifestyle precincts. Bus services along Brunswick Street and Merthyr Road provide transport connectivity.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (strict)', product: 'Sympathetic renovations, limited infill', premium: 'Limited — character restrictions' },
    { name: 'Low-Medium Density', height: '2-3 storeys', product: 'Townhouses, duplexes, granny flats', premium: 'Moderate — character area constraints' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — premium location' },
    { name: 'High Density (limited areas)', height: '8-12+ storeys', product: 'Apartment buildings along corridors', premium: 'Very high — one of Brisbane\'s most desirable suburbs' },
  ],
  mainStreets: 'Brunswick Street, Merthyr Road, James Street, and Moray Street',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.5M - $2.5M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.8M - $3.0M', perm2: '$3,200 - $4,500' },
    { type: 'HDR site (corridor)', size: '600-1,000m²', range: '$3.0M - $6.0M+', perm2: '$4,500 - $7,000+' },
  ],
  keyDriver: 'One of Brisbane\'s most prestigious residential addresses with strong underlying demand from affluent owner-occupiers',
  uniqueFactor: 'New Farm is consistently ranked among Brisbane\'s most desirable suburbs. Its tree-lined streets, heritage Queenslanders, riverside parklands, and proximity to cultural precincts create an aspirational address. Development is limited by extensive character protections, making available sites extremely rare and valuable.',
  comparison: 'New Farm\'s character protections mean development sites are among the rarest and most valuable in Brisbane. MDR and HDR sites without character overlay command extreme premiums.',
  nearbyAmenity: 'New Farm Park, Brisbane Powerhouse, Brunswick Street dining precinct, and James Street',
});
